'use client';

import { useState, useEffect, Suspense } from 'react';
import { useRouter } from 'next/navigation';
import { useSearchParams } from 'next/navigation';
import { AdvertsClassicFilter } from '@/features/adverts/components/AdvertsClassicFilter';
import { AdvertsSmartFilter } from '@/features/adverts/components/AdvertsSmartFilter';
import { PortalAdvertFilterSettingsResponseDto } from '@/api/generated/model';

type ActiveFilter = 'classic' | 'smart' | 'both';

interface Props {
    mode?: ActiveFilter;
    showNav?: boolean;
    filterSettings: PortalAdvertFilterSettingsResponseDto;
    /** VOX mic button slot z page (ziadny cross-feature import). */
    voiceButton?: React.ReactNode;
}

export function AdvertsFilter(props: Props) {
    const { minPrice, maxPrice, minArea, maxArea } = props.filterSettings;

    // state
    const [searchParams, setSearchParams] = useState(new URLSearchParams({}));
    const [activeFilter, setActiveFilter] = useState<ActiveFilter>(props?.mode ?? 'smart');
    // gate len bez explicitneho mode - vtedy sa mode cita z ?filter= az na kliente.
    // S explicitnym mode (homepage "both", VOX "classic") sa filter vykresli uz v SSR HTML,
    // inak hero cakal na hydrataciu ako 146px zlaty pas bez inputu.
    const [activeFilterLoading, setActiveFilterLoading] = useState(!props.mode);

    // Set active filter based on search params
    useEffect(() => {
        setActiveFilterLoading(false);

        const filter = searchParams.get('filter');

        if (filter) {
            if (filter === 'smart' || filter === 'classic') {
                setActiveFilter(filter as ActiveFilter);
            }
        }
    }, [searchParams]);

    if (activeFilterLoading) return <></>;

    return (
        <>
            <Suspense>
                <GetSearchParams onSearchParams={(params) => setSearchParams(params)} />
            </Suspense>
            <div className="w-full">
                {/* both = homepage hero: glass karta so smart inputom (plazma)
                    a vlozenymi klasickymi filtrami */}
                {activeFilter === 'both' && (
                    <AdvertsSmartFilter
                        hero
                        showNav={true}
                        showUseClassicFilter={false}
                        onSetClassicFilter={() => setActiveFilter('classic')}
                        filterSettings={{ minArea, maxArea, minPrice, maxPrice }}
                        voiceButton={props.voiceButton}
                    />
                )}

                {activeFilter === 'smart' && (
                    <AdvertsSmartFilter
                        showNav={false}
                        showUseClassicFilter={true}
                        onSetClassicFilter={() => setActiveFilter('classic')}
                    />
                )}

                {/* Classic filter */}
                {activeFilter === 'classic' && (
                    <AdvertsClassicFilter
                        showUseSmartFilter={true}
                        onSetSmartFilter={() => setActiveFilter('smart')}
                        filterSettings={{ minArea, maxArea, minPrice, maxPrice }}
                    />
                )}
            </div>
        </>
    );
}

function GetSearchParams({
    onSearchParams,
}: {
    onSearchParams: (params: URLSearchParams) => void;
}) {
    const searchParams = useSearchParams();
    useEffect(() => {
        if (searchParams) {
            onSearchParams(searchParams);
        }
    }, [searchParams]);
    return <></>;
}
